/* =========================================================================
   ClueVine — Lattice (mini word-grid)
   Fill a small square so every row AND every column spells a word. A few
   letters are given; deduce the rest from the crossings. Solutions are real
   word squares found by backtracking search (prefix-pruned against the
   dictionary), so the grid always resolves — and any valid square is accepted,
   not just the one we picked.
   ========================================================================= */

import type {
  Dictionary,
  DifficultyLevel,
  GenerateContext,
  HintOutcome,
  InputKey,
  MoveFeedback,
  ModeScore,
  Prng,
  PuzzleStatus,
  ResultSummary,
} from '../../engine/types';

export interface LatticeConfig {
  size: number;
  revealFraction: number;
  maxAttempts: number;
}

export interface LatticeState {
  size: number;
  solution: string[]; // size*size letters — one valid word square
  given: number[]; // pre-filled (locked) cell indices
  cells: string[]; // size*size current letters ('' = empty)
  cursor: number;
  maxAttempts: number;
  attempts: number;
  solved: boolean;
}

export type LatticeMove =
  | { type: 'set'; ch: string }
  | { type: 'remove' }
  | { type: 'move'; index: number }
  | { type: 'submit' };

export const LATTICE_DIFFICULTIES: DifficultyLevel<LatticeConfig>[] = [
  { id: 'easy', label: 'Sprout', blurb: '4×4 · more given', scoreMultiplier: 0.9, config: { size: 4, revealFraction: 0.5, maxAttempts: 6 } },
  { id: 'standard', label: 'Garden', blurb: '4×4', scoreMultiplier: 1, config: { size: 4, revealFraction: 0.38, maxAttempts: 5 } },
  { id: 'hard', label: 'Thicket', blurb: '4×4 · fewer given', scoreMultiplier: 1.35, config: { size: 4, revealFraction: 0.28, maxAttempts: 5 } },
];

// Hand-verified symmetric word square: rows & columns are card / area / rent / date.
const FALLBACK = 'cardarearentdate'.split('');

/** Find a size×size grid where every row and column is a valid word. */
export function buildSquare(prng: Prng, dict: Dictionary, size: number): string[] | null {
  const pool = dict.byLength(size);
  if (pool.length < size * 4) return null;

  for (let restart = 0; restart < 6; restart++) {
    const candidates = prng.shuffle(pool);
    const rows: string[] = [];
    let nodes = 0;

    const columnsOk = (): boolean => {
      for (let c = 0; c < size; c++) {
        let pre = '';
        for (let r = 0; r < rows.length; r++) pre += rows[r][c];
        if (rows.length === size) {
          if (!dict.isValid(pre)) return false;
        } else if (!dict.hasPrefix(pre, size)) {
          return false;
        }
      }
      return true;
    };

    const backtrack = (): boolean => {
      if (rows.length === size) return true;
      for (const w of candidates) {
        if (rows.includes(w)) continue;
        rows.push(w);
        nodes += 1;
        if (nodes > 400_000) {
          rows.pop();
          return false;
        }
        if (columnsOk() && backtrack()) return true;
        rows.pop();
      }
      return false;
    };

    if (backtrack()) return rows.flatMap((w) => w.split(''));
  }
  return null;
}

/**
 * A view of the dictionary restricted to curated answer words, so generated
 * squares cross everyday words (players may still answer with any valid word).
 */
export function answerDictionary(dict: Dictionary, size: number): Dictionary {
  const pool = [...dict.answersByLength(size)].sort();
  const set = new Set(pool);
  return {
    ...dict,
    size: pool.length,
    isValid: (w) => set.has(w.toLowerCase()),
    byLength: (len) => (len === size ? pool : []),
    hasPrefix: (prefix, len) => {
      if (len != null && len !== size) return false;
      let lo = 0;
      let hi = pool.length;
      while (lo < hi) {
        const mid = (lo + hi) >>> 1;
        if (pool[mid] < prefix) lo = mid + 1;
        else hi = mid;
      }
      return lo < pool.length && pool[lo].startsWith(prefix);
    },
  };
}

const rc = (size: number, i: number) => [Math.floor(i / size), i % size] as const;

export function rowWord(cells: string[], size: number, r: number): string {
  let w = '';
  for (let c = 0; c < size; c++) w += cells[r * size + c];
  return w;
}

export function colWord(cells: string[], size: number, c: number): string {
  let w = '';
  for (let r = 0; r < size; r++) w += cells[r * size + c];
  return w;
}

export function generate(ctx: GenerateContext<LatticeConfig>): LatticeState {
  const { size, revealFraction, maxAttempts } = ctx.difficulty.config;
  const solution =
    buildSquare(ctx.prng, answerDictionary(ctx.dictionary, size), size) ??
    buildSquare(ctx.prng, ctx.dictionary, size) ??
    FALLBACK;

  const total = size * size;
  const revealCount = Math.max(size, Math.round(total * revealFraction));
  const order = ctx.prng.shuffle(Array.from({ length: total }, (_, i) => i));
  const given = order.slice(0, revealCount).sort((a, b) => a - b);

  const cells = new Array<string>(total).fill('');
  for (const g of given) cells[g] = solution[g];

  const cursor = cells.findIndex((c) => c === '');

  return {
    size,
    solution,
    given,
    cells,
    cursor: cursor < 0 ? 0 : cursor,
    maxAttempts,
    attempts: 0,
    solved: false,
  };
}

export function isGiven(state: LatticeState, i: number): boolean {
  return state.given.includes(i);
}

export function isFull(state: LatticeState): boolean {
  return state.cells.every((c) => c !== '');
}

function matchesSolution(state: LatticeState): boolean {
  return state.cells.join('') === state.solution.join('');
}

export function allLinesValid(state: LatticeState, dict: Dictionary): boolean {
  for (let i = 0; i < state.size; i++) {
    if (!dict.isValid(rowWord(state.cells, state.size, i))) return false;
    if (!dict.isValid(colWord(state.cells, state.size, i))) return false;
  }
  return true;
}

export function isSolved(state: LatticeState, dict: Dictionary): boolean {
  return isFull(state) && (matchesSolution(state) || allLinesValid(state, dict));
}

export function status(state: LatticeState): PuzzleStatus {
  if (state.solved) return 'won';
  if (state.attempts >= state.maxAttempts) return 'lost';
  return 'playing';
}

function nextEditable(state: LatticeState, from: number): number {
  const total = state.size * state.size;
  for (let k = 1; k <= total; k++) {
    const i = (from + k) % total;
    if (!isGiven(state, i)) return i;
  }
  return from;
}

export function validateMove(state: LatticeState, move: LatticeMove): { ok: boolean; reason?: string } {
  if (move.type !== 'submit') return { ok: true };
  if (!isFull(state)) return { ok: false, reason: 'Fill the whole grid' };
  return { ok: true };
}

/** reduce is dictionary-free; the win check is applied by the mode wrapper. */
export function reduce(state: LatticeState, move: LatticeMove): LatticeState {
  switch (move.type) {
    case 'set': {
      if (!/^[a-z]$/.test(move.ch) || isGiven(state, state.cursor)) return state;
      const cells = [...state.cells];
      cells[state.cursor] = move.ch;
      return { ...state, cells, cursor: nextEditable(state, state.cursor) };
    }
    case 'remove': {
      const cells = [...state.cells];
      if (!isGiven(state, state.cursor) && cells[state.cursor] !== '') {
        cells[state.cursor] = '';
        return { ...state, cells };
      }
      // step back to the previous editable cell and clear it
      const total = state.size * state.size;
      for (let k = 1; k <= total; k++) {
        const i = (state.cursor - k + total) % total;
        if (!isGiven(state, i)) {
          cells[i] = '';
          return { ...state, cells, cursor: i };
        }
      }
      return state;
    }
    case 'move':
      if (move.index < 0 || move.index >= state.size * state.size || isGiven(state, move.index)) return state;
      return { ...state, cursor: move.index };
    default:
      return state;
  }
}

export function handleKey(state: LatticeState, key: InputKey): LatticeMove | null {
  if (key.type === 'char') {
    return isGiven(state, state.cursor) ? null : { type: 'set', ch: key.value };
  }
  if (key.type === 'backspace') return { type: 'remove' };
  return { type: 'submit' };
}

export function makeHint(state: LatticeState, _dict: Dictionary): HintOutcome<LatticeState> | null {
  if (status(state) !== 'playing') return null;
  // reveal a correct letter in an empty (or wrong) editable cell
  const total = state.size * state.size;
  let slot = -1;
  for (let i = 0; i < total; i++) {
    if (!isGiven(state, i) && state.cells[i] !== state.solution[i]) {
      slot = i;
      break;
    }
  }
  if (slot < 0) return null;
  const cells = [...state.cells];
  cells[slot] = state.solution[slot];
  const [r, c] = rc(state.size, slot);
  return {
    message: `Revealed row ${r + 1}, column ${c + 1}.`,
    penalty: 45,
    state: {
      ...state,
      cells,
      given: [...state.given, slot],
      // Don't leave the cursor on a cell that just became locked.
      cursor: state.cursor === slot ? nextEditable({ ...state, given: [...state.given, slot] }, slot) : state.cursor,
    },
  };
}

export function moveFeedback(prev: LatticeState, next: LatticeState): MoveFeedback | null {
  if (next.attempts > prev.attempts && !next.solved) {
    const left = next.maxAttempts - next.attempts;
    return {
      message: left > 0 ? `Not every line is a word — ${left} ${left === 1 ? 'try' : 'tries'} left` : 'Out of tries',
      tone: 'error',
    };
  }
  return null;
}

export function score(state: LatticeState): ModeScore {
  if (status(state) !== 'won') {
    return { base: 0, lines: [{ label: 'Not solved', value: 0, kind: 'base' }], parMs: state.size * 12000 };
  }
  const base = 200 + (state.maxAttempts - state.attempts) * 130 + state.size * 40;
  return {
    base,
    lines: [{ label: `Solved (${state.attempts} miss${state.attempts === 1 ? '' : 'es'})`, value: base, kind: 'base' }],
    parMs: state.size * 12000,
    perfect: state.attempts === 0,
  };
}

export function summarize(state: LatticeState): ResultSummary {
  const won = status(state) === 'won';
  const rows = Array.from({ length: state.size }, (_, r) => rowWord(state.solution, state.size, r).toUpperCase());
  return {
    status: won ? 'won' : 'lost',
    headline: won ? `Solved in ${state.attempts + 1}` : 'Grid unsolved',
    detail: rows.join(' · '),
    revealLabel: 'Grid',
    attempts: state.attempts,
    shareGrid: [won ? `🔲 solved in ${state.attempts + 1}` : '🔲 unsolved'],
  };
}

export function deserialize(raw: unknown): LatticeState | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Partial<LatticeState>;
  if (typeof s.size !== 'number' || !Array.isArray(s.solution) || !Array.isArray(s.cells)) return null;
  if (!Array.isArray(s.given)) return null;
  return {
    size: s.size,
    solution: s.solution.filter((c): c is string => typeof c === 'string'),
    given: s.given.filter((n): n is number => typeof n === 'number'),
    cells: s.cells.filter((c): c is string => typeof c === 'string'),
    cursor: typeof s.cursor === 'number' ? s.cursor : 0,
    maxAttempts: typeof s.maxAttempts === 'number' ? s.maxAttempts : 5,
    attempts: typeof s.attempts === 'number' ? s.attempts : 0,
    solved: Boolean(s.solved),
  };
}
